import { useEffect, useMemo, useState } from "react";

import { Link } from "react-router-dom";

import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

import BulkAddToWorkOrderModal from "../../components/workOrders/BulkAddToWorkOrderModal";
import useNotification from "../../components/notifications/useNotifications";
import Card from "../../components/shared/Card/Card";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { SearchPaletteItem } from "../../components/shared/Inputs/SearchPaletteTypes";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useBaskets from "../../data/useBaskets";
import usePartBins, { usePartBinParts } from "../../data/usePartBins";
import usePartMutations from "../../data/usePartMutations";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import { IPart } from "../../models/Part";
import { naturalCompare } from "../../utils/naturalSort";
import printHtml from "../../utils/printHtml";

// The bins a picker works out of: pick a bin, see what's in it, then move a selection of those
// parts on to a work order or the basket. Read-only as far as the bins themselves go - creating,
// renaming and deleting bins stays in Admin.
export default function CoreBins() {
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const { partBins } = usePartBins();
  const { addItemsMultiple } = useBaskets("CoreBins");
  const { bulkPatchPart } = usePartMutations();
  const { generatePartBinLabel } = useLabelGenerator();

  const [binFilter, setBinFilter] = useState("");
  const [selectedBinId, setSelectedBinId] = useState<string | null>(null);
  const [selectedPartIds, setSelectedPartIds] = useState<Set<string>>(new Set());
  const [removeAfterAction, setRemoveAfterAction] = useState(false);
  const [workOrderOpen, setWorkOrderOpen] = useState(false);

  const binParts = usePartBinParts(selectedBinId ?? undefined, !!selectedBinId);

  useEffect(() => {
    setInfo({
      name: "Bins",
      desc: "See what's in each bin and move parts on to a work order or the basket",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bins = useMemo(() => {
    const all = partBins.data?.data ?? [];
    const query = binFilter.trim().toLowerCase();
    const filtered = query ? all.filter((bin) => bin.name?.toLowerCase().includes(query)) : all;
    // Bin names are hand-numbered, so read them off the rack in order ("B2" before "B10").
    return [...filtered].sort((a, b) => naturalCompare(a.name ?? "", b.name ?? ""));
  }, [partBins.data, binFilter]);

  const selectedBin = useMemo(
    () => (partBins.data?.data ?? []).find((bin) => bin.id === selectedBinId),
    [partBins.data, selectedBinId]
  );

  const parts: IPart[] = useMemo(() => binParts.data?.data ?? [], [binParts.data]);
  const selectedParts = useMemo(
    () => parts.filter((part) => selectedPartIds.has(part.id)),
    [parts, selectedPartIds]
  );
  const allSelected = parts.length > 0 && parts.every((part) => selectedPartIds.has(part.id));

  const selectBin = (binId: string) => {
    setSelectedBinId(binId);
    setSelectedPartIds(new Set());
  };

  const togglePart = (partId: string) => {
    setSelectedPartIds((prev) => {
      const next = new Set(prev);
      if (next.has(partId)) next.delete(partId);
      else next.add(partId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedPartIds(allSelected ? new Set() : new Set(parts.map((part) => part.id)));
  };

  // Clearing the bin off a set of parts. Also the "remove after action" tail, which is why it
  // takes the ids rather than reading the selection - by then the selection may have moved on.
  const removeFromBin = (partIds: string[], options?: { silent?: boolean }) => {
    if (partIds.length === 0) return;

    bulkPatchPart.mutate(
      { partIds, data: { partBinId: null, updatePartBin: true } },
      {
        onSuccess: () => {
          if (!options?.silent) {
            addNotification({
              variant: "success",
              primaryText:
                partIds.length > 1
                  ? `Removed ${partIds.length} parts from ${selectedBin?.name ?? "the bin"}`
                  : `Removed ${partIds.length} part from ${selectedBin?.name ?? "the bin"}`,
            });
          }
          setSelectedPartIds(new Set());
        },
        onError: () =>
          addNotification({
            variant: "danger",
            primaryText: "Unable to remove those parts from the bin",
          }),
      }
    );
  };

  // Reprinting the bin's own label, same 10x5cm layout as Admin prints. Useful here because this
  // is where you find out a bin's label has gone missing or unreadable.
  const handlePrintBinLabel = async () => {
    if (!selectedBin) return;

    const html = await generatePartBinLabel(selectedBin);
    printHtml(html);
  };

  const handleAddToBasket = () => {
    const partIds = selectedParts.map((part) => part.id);
    if (partIds.length === 0) return;

    addItemsMultiple.mutate(partIds, {
      onSuccess: () => {
        addNotification({
          variant: "success",
          primaryText:
            partIds.length > 1
              ? `Added ${partIds.length} parts to the basket`
              : `Added ${partIds.length} part to the basket`,
        });
        if (removeAfterAction) removeFromBin(partIds, { silent: true });
        else setSelectedPartIds(new Set());
      },
      onError: () =>
        addNotification({ variant: "danger", primaryText: "Unable to add those parts to the basket" }),
    });
  };

  const workOrderItems: SearchPaletteItem[] = useMemo(
    () =>
      selectedParts.map((part) => ({
        id: part.id,
        name: `${part.tagNumber ?? "Unknown Tag"} - ${part.title ?? "Part"}`,
        type: "Stock",
      })),
    [selectedParts]
  );

  const isBusy = bulkPatchPart.isLoading || addItemsMultiple.isLoading;
  const actionsDisabled = selectedPartIds.size === 0 || isBusy;

  return (
    <>
      <BulkAddToWorkOrderModal
        open={workOrderOpen}
        onClose={() => setWorkOrderOpen(false)}
        selectedItems={workOrderItems}
        // Only once the work order has actually saved - see the prop's note.
        onAdded={() => {
          const partIds = selectedParts.map((part) => part.id);
          if (removeAfterAction) removeFromBin(partIds, { silent: true });
          else setSelectedPartIds(new Set());
        }}
      />

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title="Bins" bodyClassName="p-0">
          <div className="p-3">
            <input
              type="text"
              value={binFilter}
              onChange={(e) => setBinFilter(e.target.value)}
              placeholder="Search bins..."
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {partBins.isLoading ? (
            <LoadingWheel />
          ) : bins.length === 0 ? (
            <p className="px-3 pb-4 text-sm text-gray-500">
              {binFilter ? "No bins match that search." : "No bins have been set up yet."}
            </p>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto border-t border-gray-100">
              {bins.map((bin) => (
                <button
                  key={bin.id}
                  type="button"
                  onClick={() => selectBin(bin.id)}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                    selectedBinId === bin.id ? "bg-primary-50 font-medium text-primary-800" : "text-gray-800"
                  }`}
                >
                  <span className="min-w-0 flex-1 truncate">{bin.name}</span>
                  <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                    {bin.partCount ?? 0}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card
          title={selectedBin ? `Parts in ${selectedBin.name}` : "Parts"}
          subtitle={selectedBin ? `${selectedPartIds.size} of ${parts.length} selected` : undefined}
          containerClassName="lg:col-span-2"
          bodyClassName="p-4"
          headerRight={
            selectedBin ? (
              <ButtonInput
                label="Print Bin Label"
                isSubmit={false}
                onClick={() => handlePrintBinLabel()}
                classes={"!bg-white !text-gray-700 !border-gray-300 hover:!bg-gray-50"}
              />
            ) : undefined
          }
        >
          {!selectedBinId ? (
            <p className="py-10 text-center text-sm text-gray-500">Pick a bin to see what&apos;s in it.</p>
          ) : binParts.isLoading ? (
            <LoadingWheel />
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <ButtonInput
                  label="Add to Work Order"
                  isSubmit={false}
                  disabled={actionsDisabled}
                  onClick={() => setWorkOrderOpen(true)}
                  classes={undefined}
                />
                <ButtonInput
                  label="Add to Basket"
                  isSubmit={false}
                  disabled={actionsDisabled}
                  isLoading={addItemsMultiple.isLoading}
                  onClick={handleAddToBasket}
                  classes={undefined}
                />
                <ButtonInput
                  label="Remove from Bin"
                  isSubmit={false}
                  disabled={actionsDisabled}
                  isLoading={bulkPatchPart.isLoading}
                  onClick={() => removeFromBin(selectedParts.map((part) => part.id))}
                  classes={"!bg-red-500 hover:!bg-red-600"}
                />

                <label className="ml-auto flex cursor-pointer items-center gap-x-2">
                  <input
                    type="checkbox"
                    checked={removeAfterAction}
                    onChange={(e) => setRemoveAfterAction(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Remove from bin after action</span>
                </label>
              </div>

              {parts.length === 0 ? (
                <p className="py-10 text-center text-sm text-gray-500">This bin is empty.</p>
              ) : (
                <div className="overflow-hidden rounded-md border border-gray-200">
                  <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="Select all parts in this bin"
                      className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      {parts.length} part{parts.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  <ul className="max-h-[55vh] divide-y divide-gray-100 overflow-y-auto">
                    {parts.map((part) => {
                      const isSelected = selectedPartIds.has(part.id);

                      return (
                        <li
                          key={part.id}
                          className={`flex items-center gap-3 px-3 py-2 ${isSelected ? "bg-primary-50" : "hover:bg-gray-50"}`}
                        >
                          <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => togglePart(part.id)}
                              className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-gray-900">
                                {part.title ?? "Part"}
                              </span>
                              {part.tagNumber && (
                                <span className="block text-xs text-gray-500">{part.tagNumber}</span>
                              )}
                            </span>
                          </label>

                          <Link
                            to={`/stock/${part.id}`}
                            aria-label={`Open ${part.title ?? "part"}`}
                            className="shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                          >
                            <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

    </>
  );
}
